import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
    collection,
    query,
    orderBy,
    onSnapshot,
    addDoc,
    updateDoc,
    getDoc,
    doc,
    getDocs,
} from "firebase/firestore";

import UploadField from "../components/UploadField";
import { computeCoverage, deriveStatusFromCoverage } from "../utils/tracking";
import { useNavigate } from "react-router-dom";

export default function ColisCards() {
    // --------- State principal ---------
    const [list, setList] = useState([]);
    const nav = useNavigate();
    // recherche & filtres
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("en_entrepot"); // all | en_attente | en_entrepot | en_conteneur | recu

    // détails à la demande
    const [expandedId, setExpandedId] = useState(null);
    const [details, setDetails] = useState({}); // { [id]: { articles:[], lots:[] } }
    const [loadingDetails, setLoadingDetails] = useState(false);

    // modales (paiement / lot)
    const [activeColis, setActiveColis] = useState(null);
    const [showPay, setShowPay] = useState(false);
    const [showLot, setShowLot] = useState(false);

    // 🆕 modale galerie
    const [showGallery, setShowGallery] = useState(false);
    const [galleryImages, setGalleryImages] = useState([]);
    const [galleryTitle, setGalleryTitle] = useState("");
    const [loadingGallery, setLoadingGallery] = useState(false);

    const emptyPayment = {
        amount: "",
        currency: "XOF",
        rate: 1,
        method: "cash",
        note: "",
        date: new Date().toISOString(),
    };
    const [payForm, setPayForm] = useState(emptyPayment);

    const emptyLot = {
        step: "warehouse",
        qty: 1,
        note: "",
        date: new Date().toISOString(),
        images: [],
        containerNo: "",
        shippingUrl: "",
    };
    const [lotForm, setLotForm] = useState(emptyLot);

    // --------- Abonnement Firestore ---------
    useEffect(() => {
        const qy = query(collection(db, "colischine"), orderBy("createdAt", "desc"));
        const unsub = onSnapshot(qy, (snap) => {
            const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setList(arr);
        });
        return () => unsub();
    }, []);

    // --------- Recherche & filtre ---------
    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        return list.filter((c) => {
            const okStatus = statusFilter === "all" ? true : c.status === statusFilter;
            const inText = !s
                ? true
                : [c.code, c.clientName, c.clientPhone, c.origin, c.destination]
                    .filter(Boolean)
                    .some((v) => String(v).toLowerCase().includes(s));
            return okStatus && inText;
        });
    }, [list, search, statusFilter]);
    // --- Totaux basés sur la liste filtrée ---
    const totals = useMemo(() => {
        let sumEst = 0;
        let sumPaid = 0;
        for (const c of filtered) {
            sumEst += Number(c.estimateXOF || 0);
            sumPaid += Number(c.paidTotalXOF || 0);
        }
        return {
            sumEst,
            sumPaid,
            sumRest: sumEst - sumPaid,
        };
    }, [filtered]);

    const fmt = (n) => Number(n || 0).toLocaleString("fr-FR");


    // --------- Détails: chargement lazy ---------
    const toggleDetails = async (colis) => {
        if (expandedId === colis.id) {
            setExpandedId(null);
            return;
        }
        setExpandedId(colis.id);
        if (details[colis.id]) return; // déjà en cache

        try {
            setLoadingDetails(true);
            const [artSnap, lotSnap] = await Promise.all([
                getDocs(collection(db, "colischine", colis.id, "articles")),
                getDocs(collection(db, "colischine", colis.id, "lots")),
            ]);
            const articles = artSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            const lots = lotSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setDetails((prev) => ({ ...prev, [colis.id]: { articles, lots } }));
        } finally {
            setLoadingDetails(false);
        }
    };

    // --------- 🆕 Ouvrir la GALLERIE en cliquant sur la cover ---------
    const openGallery = async (colis) => {
        setShowGallery(true);
        setGalleryTitle(`${colis.code} — ${colis.clientName || ""}`);
        setLoadingGallery(true);
        try {
            let arts = details[colis.id]?.articles;
            if (!arts) {
                const artSnap = await getDocs(collection(db, "colischine", colis.id, "articles"));
                arts = artSnap.docs.map((d) => d.data());
                // on merge dans le cache des détails sans écraser d'éventuels lots déjà chargés
                setDetails((prev) => ({
                    ...prev,
                    [colis.id]: { articles: arts, lots: prev[colis.id]?.lots || [] },
                }));
            }
            const imgs = (arts || []).flatMap((a) => (Array.isArray(a.images) ? a.images : []));
            setGalleryImages(imgs);
        } finally {
            setLoadingGallery(false);
        }
    };

    // --------- Agrégats côté serveur après action ---------
    const recomputePaymentAggregates = async (colisId) => {
        const paysSnap = await getDocs(collection(db, "colischine", colisId, "payments"));
        let totalXOF = 0;
        paysSnap.forEach((d) => {
            const p = d.data();
            totalXOF += Number(p.amount || 0) * Number(p.rate || 1);
        });

        const cRef = doc(db, "colischine", colisId);
        const cSnap = await getDoc(cRef);
        const est = Number(cSnap.data()?.estimateXOF || 0);

        await updateDoc(cRef, {
            paidTotalXOF: totalXOF,
            balanceXOF: est - totalXOF,
            updatedAt: new Date(),
        });
    };

    const recomputeStatusAggregates = async (colisId) => {
        const artSnap = await getDocs(collection(db, "colischine", colisId, "articles"));
        const lotsSnap = await getDocs(collection(db, "colischine", colisId, "lots"));
        const arts = artSnap.docs.map((d) => d.data());
        const lts = lotsSnap.docs.map((d) => d.data());

        const cov = computeCoverage(arts, lts);
        let nextStatus = deriveStatusFromCoverage(cov);

        // garde-fou paiement: ne pas passer en "recu" si pas soldé
        const cRef = doc(db, "colischine", colisId);
        const cSnap = await getDoc(cRef);
        const cur = cSnap.data() || {};
        const est = Number(cur.estimateXOF || 0);
        const paid = Number(cur.paidTotalXOF || 0);
        if (nextStatus === "recu" && paid < est) nextStatus = cur.status || "en_conteneur";

        await updateDoc(cRef, { status: nextStatus, updatedAt: new Date() });
    };

    // --------- Actions rapides ---------
    const openPayModal = (colis) => {
        setActiveColis(colis);
        setPayForm({ ...emptyPayment });
        setShowPay(true);
    };
    const openLotModal = (colis) => {
        setActiveColis(colis);
        setLotForm({ ...emptyLot });
        setShowLot(true);
    };

    const submitPayment = async () => {
        if (!activeColis?.id) return;
        const path = collection(db, "colischine", activeColis.id, "payments");
        await addDoc(path, payForm);
        await recomputePaymentAggregates(activeColis.id);
        setShowPay(false);
    };

    const submitLot = async () => {
        if (!activeColis?.id) return;
        if (lotForm.step === "container" && !(lotForm.containerNo || lotForm.shippingUrl)) {
            alert("Renseigne au moins le N° de conteneur ou le lien du site de shipping.");
            return;
        }
        const path = collection(db, "colischine", activeColis.id, "lots");
        await addDoc(path, lotForm);
        await recomputeStatusAggregates(activeColis.id);
        setShowLot(false);
    };

    const markReceived = async (colis) => {
        const cRef = doc(db, "colischine", colis.id);
        const cSnap = await getDoc(cRef);
        const data = cSnap.data();
        const est = Number(data?.estimateXOF || 0);
        const paid = Number(data?.paidTotalXOF || 0);
        if (paid < est) {
            alert("Impossible de marquer 'Reçu' : le colis n'est pas totalement payé.");
            return;
        }
        await updateDoc(cRef, { status: "recu", updatedAt: new Date() });
    };

    // --------- RENDER ---------
    const StatusBadge = ({ status }) => {
        const map = {
            recu: "success",
            en_conteneur: "primary",
            en_entrepot: "warning text-dark",
            en_attente: "secondary",
        };
        const cls = map[status] || "secondary";
        const label =
            status === "recu"
                ? "Reçu"
                : status === "en_conteneur"
                    ? "En conteneur"
                    : status === "en_entrepot"
                        ? "À l'entrepôt"
                        : "En attente";
        return <span className={`badge bg-${cls}`}>{label}</span>;
    };

    const PaidBadge = ({ est = 0, paid = 0 }) => {
        if (!est && !paid) return <span className="badge bg-secondary">Pas d'estimation</span>;
        if (paid >= est) return <span className="badge bg-success">Soldé</span>;
        if (paid > 0) return <span className="badge bg-warning text-dark">Partiel</span>;
        return <span className="badge bg-secondary">Non payé</span>;
    };

    const PaidProgress = ({ est = 0, paid = 0 }) => {
        const pct = est > 0 ? Math.min(100, Math.round((paid / est) * 100)) : 0;
        return (
            <div className="progress" style={{ height: 6 }}>
                <div className="progress-bar" style={{ width: `${pct}%` }} />
            </div>
        );
    };

    return (
        <div className="container py-4">
            <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
                <div className="me-auto">
                    <h3 className="mb-0 d-flex flex-wrap align-items-center gap-2">
                        <span>📇 Colis (vue cartes)</span>
                        <span className="badge bg-light text-dark">
                            Total estimé: {fmt(totals.sumEst)} XOF
                        </span>
                        <span className="badge bg-success">
                            Encaissé: {fmt(totals.sumPaid)} XOF
                        </span>
                        <span className="badge bg-secondary">
                            Reste: {fmt(totals.sumRest)} XOF
                        </span>
                    </h3>
                    <div className="text-muted small">
                        Chine → Lomé • {filtered.length} colis
                    </div>
                </div>

                <div className="">
                    <label className="form-label small mb-1">Filtrer par statut</label>
                    <select
                        className="form-select form-select-sm"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="en_entrepot">À l'entrepôt</option>
                        <option value="en_conteneur">En conteneur</option>
                        <option value="recu">Reçu</option>
                        <option value="en_attente">En attente</option>
                        <option value="all">Tous</option>
                    </select>

                </div>
                <div style={{ minWidth: 260 }}>
                    <label className="form-label small mb-1">Recherche</label>
                    <input
                        className="form-control form-control-sm"
                        placeholder="Code, client, téléphone, origine…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3">
                {filtered.map((c) => {
                    const fullyPaid = Number(c.paidTotalXOF || 0) >= Number(c.estimateXOF || 0);
                    const isOpen = expandedId === c.id;
                    const det = details[c.id];

                    const wh = (det?.lots || [])
                        .filter((l) => l.step === "warehouse")
                        .reduce((s, l) => s + Number(l.qty || 0), 0);
                    const cont = (det?.lots || [])
                        .filter((l) => l.step === "container")
                        .reduce((s, l) => s + Number(l.qty || 0), 0);
                    const lome = (det?.lots || [])
                        .filter((l) => l.step === "lome")
                        .reduce((s, l) => s + Number(l.qty || 0), 0);

                    return (
                        <div className="col" key={c.id}>
                            <div className="card h-100 shadow-sm">
                                <img
                                    src={c.cover || "https://via.placeholder.com/640x360?text=Colis"}
                                    className="card-img-top"
                                    alt="cover"
                                    style={{ objectFit: "cover", height: 260, cursor: "zoom-in" }}
                                    onClick={() => openGallery(c)} // 🆕 Ouvrir galerie au clic sur cover
                                />
                                <div className="card-body d-flex flex-column">
                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                        <h6 className="card-title mb-0">{c.code}</h6>
                                        <StatusBadge status={c.status} />
                                    </div>
                                    <div className="text-muted small mb-1">
                                        {c.clientName} • {c.clientPhone || "—"}
                                    </div>
                                    <div className="small mb-2">
                                        {c.origin} → {c.destination}
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <PaidBadge est={Number(c.estimateXOF || 0)} paid={Number(c.paidTotalXOF || 0)} />
                                        <div className="small">
                                            {Number(c.paidTotalXOF || 0).toLocaleString("fr-FR")} /{" "}
                                            {Number(c.estimateXOF || 0).toLocaleString("fr-FR")} XOF
                                        </div>
                                    </div>
                                    <PaidProgress est={Number(c.estimateXOF || 0)} paid={Number(c.paidTotalXOF || 0)} />

                                    {/* Actions */}
                                    <div className="mt-3 d-flex flex-wrap gap-2">
                                        <button
                                            className="btn btn-sm btn-outline-dark"
                                            onClick={() => nav(`/admin/colis/${c.id}`)}
                                        >
                                            Modifier
                                        </button>
                                        {!fullyPaid && (
                                            <button className="btn btn-sm btn-outline-primary" onClick={() => openPayModal(c)}>
                                                + Paiement
                                            </button>
                                        )}
                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => openLotModal(c)}>
                                            + Étape (lot)
                                        </button>
                                        {c.status !== "recu" && (
                                            <button
                                                className="btn btn-sm btn-success"
                                                disabled={!fullyPaid}
                                                onClick={() => markReceived(c)}
                                                title={!fullyPaid ? "Le colis doit être totalement payé" : ""}
                                            >
                                                Marquer « Reçu »
                                            </button>
                                        )}
                                        <button className="btn btn-sm btn-light ms-auto" onClick={() => toggleDetails(c)}>
                                            {isOpen ? "Masquer détails" : "Voir détails"}
                                        </button>
                                    </div>

                                    {/* Détails */}
                                    {isOpen && (
                                        <div className="mt-3 border-top pt-3">
                                            {loadingDetails && !det && (
                                                <div className="text-muted small">Chargement des détails…</div>
                                            )}

                                            {det && (
                                                <>
                                                    {/* ARTICLES */}
                                                    <div className="table-responsive">
                                                        <table className="table table-sm align-middle">
                                                            <thead>
                                                                <tr>
                                                                    <th>Article</th>
                                                                    <th className="text-end">Qté</th>
                                                                    <th className="text-end">Poids/Taille</th>
                                                                    <th className="text-end">Valeur unitaire (XOF)</th>
                                                                    <th className="text-end">Valeur ligne (XOF)</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {det.articles?.map((a) => {
                                                                    const unit = Number(a.unitValueXOF ?? a.value ?? 0);
                                                                    const qty = Number(a.qty || 0);
                                                                    const line = qty * unit;
                                                                    return (
                                                                        <tr key={a.id}>
                                                                            <td>
                                                                                <div className="d-flex align-items-center gap-2">
                                                                                    {Array.isArray(a.images) && a.images[0] && (
                                                                                        <img
                                                                                            src={a.images[0]}
                                                                                            alt=""
                                                                                            width={32}
                                                                                            height={32}
                                                                                            className="rounded border"
                                                                                            style={{ objectFit: "cover" }}
                                                                                        />
                                                                                    )}
                                                                                    <span>{a.title || "-"}</span>
                                                                                </div>
                                                                            </td>
                                                                            <td className="text-end">{qty}</td>
                                                                            <td className="text-end">{a.weight || "-"}</td>
                                                                            <td className="text-end">{unit.toLocaleString("fr-FR")}</td>
                                                                            <td className="text-end fw-semibold">{line.toLocaleString("fr-FR")}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                                {(!det.articles || det.articles.length === 0) && (
                                                                    <tr>
                                                                        <td colSpan={5} className="text-center text-muted">
                                                                            Aucun article.
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* ÉTAPES + INFO CONTENEUR */}
                                                    <div className="small">
                                                        <span className="me-3">Étapes :</span>
                                                        <span className="me-3">
                                                            Entrepôt (Chine) : <strong>{wh}</strong>
                                                        </span>
                                                        <span className="me-3">
                                                            Conteneur : <strong>{cont}</strong>
                                                        </span>
                                                        <span>
                                                            Réception à Lomé : <strong>{lome}</strong>
                                                        </span>
                                                    </div>

                                                    {(det?.lots || []).some(
                                                        (l) => l.step === "container" && (l.containerNo || l.shippingUrl)
                                                    ) && (
                                                            <div className="mt-2 small">
                                                                <div className="fw-semibold mb-1">Conteneurs :</div>
                                                                <ul className="mb-0">
                                                                    {(det.lots || [])
                                                                        .filter((l) => l.step === "container" && (l.containerNo || l.shippingUrl))
                                                                        .map((l) => (
                                                                            <li key={l.id}>
                                                                                {l.containerNo && (
                                                                                    <span className="me-2">
                                                                                        N° <strong>{l.containerNo}</strong>
                                                                                    </span>
                                                                                )}
                                                                                {l.shippingUrl && (
                                                                                    <a href={l.shippingUrl} target="_blank" rel="noopener noreferrer">
                                                                                        Ouvrir le site de shipping
                                                                                    </a>
                                                                                )}
                                                                            </li>
                                                                        ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {filtered.length === 0 && (
                <div className="text-center text-muted py-5">Aucun colis pour ces critères.</div>
            )}

            {/* ======= MODALE PAIEMENT ======= */}
            {showPay && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Ajouter un paiement — {activeColis?.code}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowPay(false)} />
                            </div>
                            <div className="modal-body">
                                <div className="row g-2">
                                    <div className="col-6">
                                        <label className="form-label">Montant</label>
                                        <input
                                            className="form-control"
                                            value={payForm.amount}
                                            onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Devise</label>
                                        <select
                                            className="form-select"
                                            value={payForm.currency}
                                            onChange={(e) => setPayForm({ ...payForm, currency: e.target.value })}
                                        >
                                            <option>XOF</option>
                                            <option>USD</option>
                                            <option>CNY</option>
                                            <option>EUR</option>
                                        </select>
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Taux</label>
                                        <input
                                            className="form-control"
                                            value={payForm.rate}
                                            onChange={(e) => setPayForm({ ...payForm, rate: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Méthode</label>
                                        <select
                                            className="form-select"
                                            value={payForm.method}
                                            onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}
                                        >
                                            <option value="cash">Cash</option>
                                            <option value="mobile_money">Mobile Money</option>
                                            <option value="bank">Virement</option>
                                            <option value="wechat">WeChat</option>
                                            <option value="alipay">Alipay</option>
                                        </select>
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label">Note</label>
                                        <input
                                            className="form-control"
                                            value={payForm.note}
                                            onChange={(e) => setPayForm({ ...payForm, note: e.target.value })}
                                            placeholder="Réf, tranche 2, etc."
                                        />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label">Date</label>
                                        <input
                                            type="datetime-local"
                                            className="form-control"
                                            value={payForm.date?.slice(0, 16)}
                                            onChange={(e) =>
                                                setPayForm({ ...payForm, date: new Date(e.target.value).toISOString() })
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowPay(false)}>
                                    Annuler
                                </button>
                                <button className="btn btn-primary" onClick={submitPayment}>
                                    Enregistrer le paiement
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ======= MODALE ÉTAPE (LOT) ======= */}
            {showLot && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.5)" }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Ajouter une étape (lot) — {activeColis?.code}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowLot(false)} />
                            </div>
                            <div className="modal-body">
                                <div className="row g-2">
                                    <div className="col-md-4">
                                        <label className="form-label">Étape</label>
                                        <select
                                            className="form-select"
                                            value={lotForm.step}
                                            onChange={(e) => setLotForm({ ...lotForm, step: e.target.value })}
                                        >
                                            <option value="warehouse">Entrepôt (Chine)</option>
                                            <option value="container">Mise en conteneur</option>
                                            <option value="lome">Réception à Lomé</option>
                                        </select>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">Quantité</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={lotForm.qty}
                                            onChange={(e) => setLotForm({ ...lotForm, qty: Number(e.target.value || 0) })}
                                        />
                                    </div>
                                    <div className="col-md-5">
                                        <label className="form-label">Note</label>
                                        <input
                                            className="form-control"
                                            value={lotForm.note}
                                            onChange={(e) => setLotForm({ ...lotForm, note: e.target.value })}
                                            placeholder="Ex: 2 cartons déposés à l'entrepôt"
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Date</label>
                                        <input
                                            type="datetime-local"
                                            className="form-control"
                                            value={lotForm.date?.slice(0, 16)}
                                            onChange={(e) =>
                                                setLotForm({ ...lotForm, date: new Date(e.target.value).toISOString() })
                                            }
                                        />
                                    </div>

                                    {lotForm.step === "container" && (
                                        <>
                                            <div className="col-md-6">
                                                <label className="form-label">N° de conteneur</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Ex: MSCU1234567"
                                                    value={lotForm.containerNo}
                                                    onChange={(e) => setLotForm({ ...lotForm, containerNo: e.target.value })}
                                                />
                                            </div>
                                            <div className="col-md-12">
                                                <label className="form-label">Lien du site de shipping</label>
                                                <input
                                                    type="url"
                                                    className="form-control"
                                                    placeholder="https://..."
                                                    value={lotForm.shippingUrl}
                                                    onChange={(e) => setLotForm({ ...lotForm, shippingUrl: e.target.value })}
                                                />
                                            </div>
                                        </>
                                    )}

                                    <div className="col-md-12">
                                        <label className="form-label">Preuves (images)</label>
                                        <UploadField
                                            pathPrefix="colis/lots"
                                            onUploaded={(url) =>
                                                setLotForm((f) => ({ ...f, images: [...(f.images || []), url] }))
                                            }
                                        />
                                        {(lotForm.images || []).length > 0 && (
                                            <div className="d-flex flex-wrap gap-2 mt-2">
                                                {lotForm.images.map((u, idx) => (
                                                    <div key={idx} className="position-relative">
                                                        <img
                                                            src={u}
                                                            alt="lot"
                                                            style={{ width: 72, height: 72, objectFit: "cover" }}
                                                            className="rounded border"
                                                        />
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-light position-absolute top-0 end-0"
                                                            onClick={() =>
                                                                setLotForm((f) => ({ ...f, images: f.images.filter((_, k) => k !== idx) }))
                                                            }
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowLot(false)}>
                                    Annuler
                                </button>
                                <button className="btn btn-primary" onClick={submitLot}>
                                    Enregistrer l’étape
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ======= 🆕 MODALE GALERIE IMAGES D'ARTICLES ======= */}
            {showGallery && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.5)" }}>
                    <div className="modal-dialog modal-xl">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Images des articles — {galleryTitle}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowGallery(false)} />
                            </div>
                            <div className="modal-body">
                                {loadingGallery ? (
                                    <div className="text-muted">Chargement…</div>
                                ) : galleryImages.length > 0 ? (
                                    <div className="row g-2">
                                        {galleryImages.map((u, i) => (
                                            <div className="col-6 col-md-4 col-lg-3" key={i}>
                                                <img
                                                    src={u}
                                                    alt={`article-${i}`}
                                                    className="img-fluid rounded border"
                                                    style={{ width: "100%", objectFit: "cover", aspectRatio: "1/1" }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-muted">Aucune image d’article pour ce colis.</div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowGallery(false)}>
                                    Fermer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
